import React, { useMemo } from 'react';
import { ChevronRight, ChevronLeft, Plus } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import './UserSidebar.css';

/**
 * Sidebar for the admin consoles — the user dashboard's panel, photo and
 * collapse behaviour, with flat sections instead of expandable shipment groups.
 *
 * `sections` is [{ title, items: [{ id, label, icon, count }] }].
 */
const AdminSidebar = ({
  sections = [],
  activeId,
  onSelect,
  role = 'Admin',
  cta,                       // { label, icon, onClick, id }
  collapsed,
  onToggleCollapse,
  mobileOpen,
  onCloseMobile,
}) => {
  const { isDarkMode } = useTheme();
  const art = isDarkMode
    ? '/images/theme-assets/sidebar-dark.png'
    : '/images/theme-assets/sidebar-light.png';

  const cls = useMemo(() => [
    'ql-side',
    collapsed ? 'is-collapsed' : '',
    mobileOpen ? 'is-open' : '',
  ].filter(Boolean).join(' '), [collapsed, mobileOpen]);

  const pick = (item) => { onSelect?.(item.id); onCloseMobile?.(); };
  const CtaIcon = cta?.icon || Plus;

  return (
    <>
      {mobileOpen && <button type="button" className="ql-side__scrim lg:hidden" aria-label="Close menu" onClick={onCloseMobile} />}

      <aside className={cls}>
        <div className="ql-side__art" style={{ backgroundImage: `url(${art})` }} aria-hidden="true" />

        <button
          type="button"
          className="ql-side__toggle"
          onClick={onToggleCollapse}
          title={collapsed ? 'Expand menu' : 'Collapse menu'}
          aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}
        >
          {collapsed ? <ChevronRight size={15} /> : <ChevronLeft size={15} />}
        </button>

        <div className="ql-side__body">
          <div className="ql-side__brand" style={{ flexDirection: 'column', gap: 0 }}>
            <img src="/images/logo.png" alt="QuickLook" />
            <span className="ql-side__role">{role}</span>
          </div>

          {cta && (
            <div style={{ padding: collapsed ? '4px 10px 0' : '4px 14px 0' }}>
              <button
                type="button"
                className={`ql-side__item ql-side__cta${cta.id && activeId === cta.id ? ' is-active' : ''}`}
                onClick={cta.onClick}
                title={collapsed ? cta.label : undefined}
                style={{ width: '100%' }}
              >
                <span className="ql-side__icon"><CtaIcon size={17} /></span>
                <span className="ql-side__label">{cta.label}</span>
              </button>
            </div>
          )}

          <nav className="ql-side__nav">
            {sections.map((section) => (
              <div key={section.title}>
                <p className="ql-side__section">{section.title}</p>
                {section.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      className={`ql-side__item${activeId === item.id ? ' is-active' : ''}`}
                      onClick={() => pick(item)}
                      title={collapsed ? item.label : undefined}
                    >
                      <span className="ql-side__icon"><Icon size={18} /></span>
                      <span className="ql-side__label">{item.label}</span>
                      {item.count > 0 && <span className="ql-side__count">{item.count}</span>}
                    </button>
                  );
                })}
              </div>
            ))}
          </nav>

        </div>
      </aside>
    </>
  );
};

export default AdminSidebar;
